import { useEffect, useRef, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { FilePlus2, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { ProgramPageRenderer } from "@/components/programPage/ProgramPageRenderer";
import {
  PROGRAM_PAGE_TEMPLATES,
  blankDocument,
  buildFromTemplate,
} from "@/components/programPage/templates";
import { programPageService, type ProgramPageEditorBootstrap } from "@/services/programPage.service";
import type { ProgramPageDocument } from "@/types/programPage";
import { ProgramPageEditor } from "./ProgramPageEditor";

/**
 * Brand-side program page builder.
 *
 * Three states: not on a plan that includes it (upgrade prompt), entitled with
 * nothing designed yet (template picker), and the editor itself.
 */
export default function ProgramPage() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ["program-page", "me"],
    queryFn: programPageService.getMine,
    // The editor owns the document once it is open; a background refetch must
    // not yank it out from under the brand mid-edit.
    refetchOnWindowFocus: false,
    staleTime: Infinity,
  });
  // Set by the template picker, or by "start over" (null re-opens the picker).
  const [chosen, setChosen] = useState<ProgramPageDocument | null | undefined>(undefined);

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Spinner />
      </div>
    );
  }
  if (isError || !data) {
    return <p className="p-8 text-sm text-muted-foreground">We could not load your program page. Try again shortly.</p>;
  }
  if (!data.entitled) return <UpgradePrompt />;

  const document = chosen === undefined ? data.page?.draft ?? null : chosen;
  if (!document) {
    return <TemplatePicker bootstrap={data} hasDraft={!!data.page?.draft} onPick={setChosen} onCancel={() => setChosen(undefined)} />;
  }
  return (
    <ProgramPageEditor
      // A new template is a new editing session.
      key={chosen ? "picked" : "saved"}
      bootstrap={data}
      initialDocument={document}
      onStartOver={() => setChosen(null)}
    />
  );
}

function UpgradePrompt() {
  return (
    <div className="mx-auto flex max-w-lg flex-col items-center gap-4 px-6 py-24 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-100 text-brand-700 dark:bg-brand-500/20 dark:text-brand-300">
        <Trophy className="h-7 w-7" />
      </span>
      <h1 className="text-2xl font-semibold">Give your tiers a page of their own</h1>
      <p className="text-muted-foreground">
        Design a branded page that shows affiliates every tier, what it pays and exactly how far they are from the
        next one. Your colours, your icons, your words. Included with full white-label.
      </p>
      <Button asChild>
        <Link to="/brand/pricing">See plans</Link>
      </Button>
    </div>
  );
}

const THUMB_WIDTH = 1000;

/**
 * The real renderer, laid out at desktop width and scaled to fit the card, so
 * a thumbnail can never drift from the template it advertises.
 */
function ScaledThumbnail({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.5);
  useEffect(() => {
    if (!ref.current) return;
    const observer = new ResizeObserver(([entry]) => setScale(entry.contentRect.width / THUMB_WIDTH));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div ref={ref} className="pointer-events-none h-64 overflow-hidden" aria-hidden>
      <div style={{ width: THUMB_WIDTH, transform: `scale(${scale})`, transformOrigin: "top left" }}>
        {children}
      </div>
    </div>
  );
}

function TemplatePicker({
  bootstrap,
  hasDraft,
  onPick,
  onCancel,
}: {
  bootstrap: ProgramPageEditorBootstrap;
  hasDraft: boolean;
  onPick: (document: ProgramPageDocument) => void;
  onCancel: () => void;
}) {
  const program = bootstrap.programs.find((p) => p.isDefault) ?? bootstrap.programs[0] ?? null;
  const context = { brandName: bootstrap.brand.companyName, program };

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-8 px-6 py-10">
      <header className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold">Pick a starting point</h1>
        <p className="max-w-2xl text-muted-foreground">
          {program?.tiers.length
            ? `Each one is already filled with your ${program.tiers.length} commission tiers and uses your portal's theme. Everything can be changed afterwards — layout, words, icons, images.`
            : "Each one uses your portal's theme. Everything can be changed afterwards — layout, words, icons, images."}
          {hasDraft && " Picking one replaces your current draft once you save."}
        </p>
      </header>

      <div className="grid gap-6 sm:grid-cols-2">
        {PROGRAM_PAGE_TEMPLATES.map((template) => {
          const document = buildFromTemplate(template, context);
          return (
            <button
              key={template.key}
              type="button"
              onClick={() => onPick(document)}
              className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card text-left transition-shadow hover:border-brand-400 hover:shadow-lg"
            >
              <ScaledThumbnail>
                <ProgramPageRenderer
                  document={document}
                  programs={bootstrap.programs}
                  defaultProgramId={program?.id ?? null}
                  joinUrl="#"
                  onSelectBlock={() => {}}
                />
              </ScaledThumbnail>
              <div className="flex flex-col gap-1 border-t border-border p-4">
                <span className="font-semibold group-hover:text-brand-700 dark:group-hover:text-brand-300">{template.name}</span>
                <span className="text-sm text-muted-foreground">{template.description}</span>
              </div>
            </button>
          );
        })}
      </div>

      <div className="flex items-center gap-4">
        <Button variant="outline" onClick={() => onPick(blankDocument())}>
          <FilePlus2 className="mr-2 h-4 w-4" /> Start from a blank page
        </Button>
        {hasDraft && (
          <button type="button" onClick={onCancel} className="text-sm text-muted-foreground underline hover:text-foreground">
            Keep my current draft
          </button>
        )}
      </div>
    </div>
  );
}
